import {useUpmPlant} from '@hconnect/upm'
import {Loading} from '@hm/fundamental'
import {useTranslation} from 'react-i18next'

import {ShutdownDetailCollapsible} from './ShutdownDetailCollapsible'
import {ShutdownDetailComments} from './ShutdownDetailComments'
import {ShutdownDetailDocuments} from './ShutdownDetailDocuments'
import {ShutdownDetailInfo, ShutdownDetailInfoActions} from './ShutdownDetailInfo'
import {StaticPartDetailDrawing} from './StaticPartDetailDrawing'

import {ShutdownStatus} from '@refra/modules/common/enums'
import {useKilns, useShutdowns} from '@refra/modules/common/providers'
import {useStaticPartReplacementQuery} from '@refra/modules/refra-static-parts/hooks/queries'
import {StaticPartComponentDto} from '@refra/modules/refra-static-parts/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface ShutdownDetailProps {
  selectedComponent: StaticPartComponentDto
}
export const ShutdownDetail = ({selectedComponent}: ShutdownDetailProps) => {
  const plantCode = useUrlParam('plantCode')
  const staticPartId = useUrlParam('staticPartId')
  const upmPlant = useUpmPlant({plantId: plantCode})
  const {selectedShutdown} = useShutdowns()
  const {selectedKiln} = useKilns()

  const {t} = useTranslation()
  const isShutdownEditable = selectedShutdown?.shutdownStatus === ShutdownStatus.Draft

  const {data: replacements, isLoading: isReplacementLoading} = useStaticPartReplacementQuery({
    plantUpmId: upmPlant.upmId,
    kilnUpmId: selectedKiln.upmId,
    shutdownId: selectedShutdown?.id ?? 0,
    componentId: selectedComponent?.id ?? 0,
    enabled: selectedShutdown !== undefined && selectedComponent !== undefined
  })

  if (isReplacementLoading) {
    return <Loading size="large" data-cy="static_part_shutdown_detail_loader" />
  }

  const replacement = replacements ? replacements[0] : undefined

  return (
    <div className="flex flex-col gap-4">
      <ShutdownDetailCollapsible
        title={t('staticParts.replacementDetail.title')}
        defaultOpen={true}
        rightSlot={
          <ShutdownDetailInfoActions
            selectedComponent={selectedComponent}
            replacementId={replacement?.id}
          />
        }
      >
        <ShutdownDetailInfo replacement={replacement} />
        <StaticPartDetailDrawing
          selectedComponent={selectedComponent}
          selectedAreas={
            (replacement?.materialInstallations ?? [])
              .map((materialInstallation) => materialInstallation.sections)
              .filter((sections) => !!sections) ?? []
          }
        />
      </ShutdownDetailCollapsible>
      {selectedShutdown && (
        <ShutdownDetailDocuments
          staticPartId={Number(staticPartId)}
          shutdownId={selectedShutdown.id}
          isReadOnly={!isShutdownEditable}
        />
      )}
      {selectedShutdown && (
        <ShutdownDetailComments
          shutdownId={selectedShutdown.id}
          kilnUpmId={selectedKiln.upmId}
          staticPartId={staticPartId}
          isReadOnly={!isShutdownEditable}
        />
      )}
    </div>
  )
}
